/**
 * PUBLIC-ROUTES-1 — the ONE place that says which routes are public (crawlable) and what
 * title + meta description each one carries.
 *
 * WHY IT EXISTS (s216, SEO phase 1). Before this, `client/index.html` carried ONE title and
 * ONE description for every URL on the domain — `/security` and `/privacy-policy` both
 * advertised the home page's. Per-route metadata needs a list of routes, and three separate
 * consumers need the SAME list: the `Seo` component (sets the tags), `sitemap.xml` (lists the
 * URLs), and the parity test (proves the two agree). A second copy of this list is how the
 * sitemap starts advertising a route that no longer exists.
 *
 * 🔴 `sitemap.xml` is a STATIC file in `client/public/` — it cannot import this module. The
 * parity test `seo-metadata.test.ts` is what keeps them honest; if you add a route here and
 * the sitemap does not list it, that test fails.
 *
 * WHAT BELONGS HERE: a public route's path, title, description and whether it is indexed.
 * WHAT DOES NOT: authenticated app routes. They are behind a login, they are never crawled,
 * and enumerating them here would be a hand-maintained list that drifts (the reason the real
 * 404 status was NOT implemented in phase 1 — see NotFoundPage).
 *
 * 🔴 Descriptions are capped at 155 characters because that is where a search result cuts.
 * The cap is ENFORCED by `seo-metadata.test.ts`, with ONE declared exception: the home
 * page's description is prescribed verbatim by the marketing ask (s216) and measures 170.
 * It is recorded as an exception rather than silently trimmed — trimming prescribed
 * marketing copy is Reade's call, not the build's (MARKETING-TRUTH-1).
 */

import { DOMAIN, PRODUCT_NAME } from '../brand';

/**
 * Where the site lives. Built from `DOMAIN.primary` (BRAND-TOKENS-1) with the `www` host the
 * canonical uses — never a typed-out domain, so a rename moves this with the brand.
 */
export const SITE_ORIGIN = `https://www.${DOMAIN.primary}`;

export interface PublicRoute {
  /** The path, exactly as the router matches it. */
  path: string;
  /** The `<title>`. */
  title: string;
  /** The `<meta name="description">`. */
  description: string;
  /** In the sitemap and indexable. False = reachable but not advertised. */
  indexed: boolean;
}

/**
 * 🔴 The home page's description is PRESCRIBED VERBATIM by the s216 marketing ask and is the
 * one declared exception to the 155-character cap (measured: 170). Do not trim it here — the
 * trim is a copy ruling, and the report hands Reade a ≤155 option to choose from.
 */
export const HOME_DESCRIPTION =
  "Estate planning software for Canadians. Organize your estate in one place, and give your executor every step, letter and form — ready when it's needed. 30-day free trial.";

/** Paths exempt from the 155-character description cap, with the reason. */
export const DESCRIPTION_CAP_EXCEPTIONS: Readonly<Record<string, string>> = {
  '/': 'Prescribed verbatim by the s216 marketing ask (measures 170). Trim is a copy ruling.',
};

export const DESCRIPTION_MAX_LENGTH = 155;

/**
 * THE DECLARATION. The six routes the s216 ask names, in sitemap order, plus the two public
 * routes that already existed and are reachable but were NOT in the ask's six
 * (`/terms-of-service`, `/support`) — they get titles so they stop advertising the home
 * page's, and `indexed: false` keeps the sitemap exactly the six the ask specified.
 */
export const PUBLIC_ROUTES: readonly PublicRoute[] = [
  {
    path: '/',
    title: `${PRODUCT_NAME} — Protect the people you love`,
    description: HOME_DESCRIPTION,
    indexed: true,
  },
  {
    path: '/pricing',
    title: `Pricing — ${PRODUCT_NAME}`,
    // 🔴 No price (D-217.3, s217). A meta description is a public surface — it is what a search
    // result SHOWS, so a price left here would outlive the page's own removal in the index.
    // PRODUCT_NAME, never a literal (brand-tokens-parity.test.ts).
    description: `How ${PRODUCT_NAME} pricing will work, and how to start with the free trial.`,
    indexed: true,
  },
  {
    path: '/advisors',
    title: `For advisors and estate lawyers — ${PRODUCT_NAME}`,
    description:
      'Refer clients with your own discount code and follow their progress in a private advisor dashboard. Completion status only, never their data.',
    indexed: true,
  },
  {
    path: '/security',
    title: `Security — ${PRODUCT_NAME}`,
    description:
      `How ${PRODUCT_NAME} protects your estate plan: encryption in transit and at rest, and a Secure Information File that keeps passwords on your device.`,
    indexed: true,
  },
  {
    path: '/privacy-policy',
    title: `Privacy policy — ${PRODUCT_NAME}`,
    description:
      `What ${PRODUCT_NAME} collects, why, where it is stored, who can see it, and how to have it deleted. Written for Canadian privacy law.`,
    indexed: true,
  },
  {
    path: '/contact',
    title: `Contact — ${PRODUCT_NAME}`,
    description:
      `Get in touch with ${PRODUCT_NAME} — for questions about your plan, or to partner with us as an advisor, estate lawyer or trust company.`,
    indexed: true,
  },
  {
    path: '/terms-of-service',
    title: `Terms of service — ${PRODUCT_NAME}`,
    description:
      `The terms that govern your use of ${PRODUCT_NAME}, including what the product does, what it does not do, and the limits of our responsibility.`,
    indexed: false,
  },
  {
    path: '/support',
    title: `Support — ${PRODUCT_NAME}`,
    description:
      `Help with your ${PRODUCT_NAME} plan — common questions, how to reach us, and what to do if you are locked out of your account.`,
    indexed: false,
  },
] as const;

const BY_PATH: ReadonlyMap<string, PublicRoute> = new Map(PUBLIC_ROUTES.map((r) => [r.path, r]));

/** The metadata for a public path, or undefined when the path is not a declared public route. */
export function publicRouteFor(path: string): PublicRoute | undefined {
  return BY_PATH.get(path);
}

/** The routes that belong in `sitemap.xml`, in order. */
export function indexedRoutes(): readonly PublicRoute[] {
  return PUBLIC_ROUTES.filter((r) => r.indexed);
}

/** Absolute URL for a route path. `/` maps to the bare origin with a trailing slash. */
export function absoluteUrl(path: string): string {
  return path === '/' ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${path}`;
}

/**
 * The first path segment of every AUTHENTICATED app route, enumerated from the four role
 * dashboards' `<Route path=…>` declarations (measured s216, 20 roots).
 *
 * 🔴 WHAT THIS IS FOR, and its failure mode. It decides ONE thing: when a logged-OUT visitor
 * hits an unmatched path, do we send them to `/login` (as before) or show the 404? An app path
 * keeps the old redirect — a bookmark to `/dashboard` must still offer a sign-in — and anything
 * else gets the `noindex` 404 instead of an indexable login page.
 *
 * 🔴 It is a HAND-MAINTAINED list, so say what happens when it goes stale: a NEW app route that
 * is not added here shows a logged-out visitor the 404 page instead of the login form. That is
 * mildly annoying and instantly visible, and the 404 offers a Sign in link so nobody is stranded.
 * It is deliberately the gentle direction — the opposite arrangement (defaulting unknown paths to
 * the redirect) is what keeps junk paths indexable, and a server-side 404 status would be the
 * dangerous direction (a real page silently answering 404 to monitors — see NotFoundPage).
 *
 * This list is NOT used for anything else. It never gates access; `AuthShell` and the role
 * dashboards remain the only authorities on what a route renders.
 */
export const APP_ROUTE_PREFIXES: readonly string[] = [
  'admin',
  'advisor',
  'audit-log',
  'categories',
  'category',
  'client',
  'dashboard',
  'estate-summary',
  'executor',
  'executors',
  'export',
  'feedback',
  'form-builder',
  'government-forms',
  'guide',
  'review-with-executor',
  'settings',
  'template-audit',
  'users',
  // Authenticated-app entry points that live outside the role dashboards.
  'ff-welcome',
  'upload',
  'verify-email',
] as const;

/** Does this path belong to the authenticated app (so an unmatched hit should offer sign-in)? */
export function isAppRoutePath(path: string): boolean {
  const first = path.replace(/^\/+/, '').split('/')[0];
  return APP_ROUTE_PREFIXES.includes(first);
}
